import React from "react";
import { getLiveInstrumentIntraday, getMarketSeries } from "../../api/client";

function todayInCnTz() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Shanghai" });
}

/**
 * Instruments with a direct live price feed may append today's provisional
 * candle to their own detail page. Industry series deliberately do not call
 * this helper: sparse accumulated industry batches are not a 240-bucket proxy.
 */
export async function appendLiveTodayCandle(data, code, kind = "instrument", referenceClose = null) {
  const entry = data?.data?.series?.[0];
  const points = entry?.points;
  if (!points?.length) return;
  const today = todayInCnTz();
  if (points[points.length - 1].t === today) return;
  const previousClose = Number.isFinite(Number(referenceClose))
    ? Number(referenceClose)
    : Number(points[points.length - 1].raw);
  if (!Number.isFinite(previousClose) || previousClose === 0) return;
  let live;
  try {
    live = await getLiveInstrumentIntraday({ code, kind, previousClose });
  } catch {
    return;
  }
  const livePoints = live?.data?.series?.[0]?.points;
  if (!livePoints?.length) return;
  const levels = livePoints.map((point) => Number(point.raw)).filter(Number.isFinite);
  if (!levels.length) return;
  points.push({
    t: today,
    open: levels[0],
    high: Math.max(...levels),
    low: Math.min(...levels),
    raw: levels[levels.length - 1],
    volume: null,
    bar_status: "partial_intraday",
  });
}

/**
 * Published minute partitions only exist after the 22:00 EOD sync. Mid-session,
 * a lone (no compare) intraday request for the current industry falls back to
 * the already-accumulated market batches instead of showing an empty chart.
 * These sparse batches are deliberately never promoted to a daily candle:
 * only an official daily bar or a complete 240-bucket pinned proxy may supply
 * industry daily OHLC.
 */
export function useIndustrySeriesWithLiveFallback({
  industryId,
  industryCode,
  period,
  selectedComparisons,
  snapshotId,
  refreshKey,
  previousClose = null,
}) {
  const [seriesPayload, setSeriesPayload] = React.useState(null);
  const [seriesLoading, setSeriesLoading] = React.useState(true);
  const [seriesError, setSeriesError] = React.useState("");

  React.useEffect(() => {
    if (!industryId) return undefined;
    let active = true;
    const instrumentIds = [industryId, ...selectedComparisons.map((item) => item.instrument_id)];
    setSeriesLoading(true);
    setSeriesError("");
    (async () => {
      try {
        const data = await getMarketSeries({
          snapshotId,
          instrumentIds,
          period,
          // A published minute snapshot pinned to a past business date would
          // otherwise be returned as "the" intraday line; forcing today's
          // date makes a stale-but-successful response fail instead, so the
          // live fallback below actually engages once the EOD sync is behind.
          tradeDate: period === "intraday" ? todayInCnTz() : null,
          normalization: instrumentIds.length > 1 ? "common_start_100" : "none",
        });
        if (active) setSeriesPayload(data);
        return;
      } catch (requestError) {
        if (period !== "intraday" || instrumentIds.length !== 1) {
          if (active) {
            setSeriesPayload(null);
            setSeriesError(requestError.message);
          }
          return;
        }
      }
      try {
        const liveData = await getLiveInstrumentIntraday({ code: industryCode, kind: "industry", previousClose });
        if (active) setSeriesPayload(liveData);
      } catch (liveError) {
        if (active) {
          setSeriesPayload(null);
          setSeriesError(liveError.message);
        }
      }
    })().finally(() => {
      if (active) setSeriesLoading(false);
    });
    return () => {
      active = false;
    };
  }, [industryId, industryCode, period, selectedComparisons, refreshKey, snapshotId, previousClose]);

  return { seriesPayload, seriesLoading, seriesError };
}
